import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import WorkspaceGrid from "@/components/WorkspaceGrid";
import DrilldownModal from "@/components/DrilldownModal";
import { Logo, ThemeToggle } from "@/components/AppShell";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Eye, AlertCircle } from "lucide-react";

export default function SharedWorkspacePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [drillItem, setDrillItem] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: shared }, { data: summary }] = await Promise.all([
          api.get(`/shared/${token}`),
          api.get(`/shared/${token}/accounts-summary`),
        ]);
        setData(shared);
        setAccounts(summary);
      } catch (e) {
        setError(e?.response?.status === 404 ? "This share link doesn't exist or was revoked." : "Couldn't load this workspace.");
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-4 text-center">
        <AlertCircle className="h-8 w-8 text-[hsl(var(--warning))]" />
        <h1 className="font-display text-xl font-semibold">{error}</h1>
        <p className="text-sm text-muted-foreground">Ask the owner for a fresh link.</p>
        <Button asChild variant="secondary"><Link to="/">Go to My Trading Room</Link></Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen space-y-4 bg-background p-6">
        <Skeleton className="h-12 w-72" />
        <div className="grid grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-36" />)}
        </div>
      </div>
    );
  }

  const ws = data.workspace;
  const defaults = { account_id: "demo-scalper", range: "all", ...(ws.settings || {}) };

  return (
    <div className="flex min-h-screen flex-col bg-background" data-testid="shared-workspace-page">
      <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur">
        <div className="flex h-14 items-center gap-3 px-4 lg:px-6">
          <Link to="/"><Logo /></Link>
          <div className="ml-2 hidden min-w-0 sm:block">
            <div className="truncate font-display text-sm font-semibold">{ws.name}</div>
            <div className="text-[11px] text-muted-foreground">shared by {data.owner?.name}</div>
          </div>
          <Badge variant="secondary" className="ml-2 gap-1 text-[10px]" data-testid="read-only-badge">
            <Eye className="h-3 w-3" /> Read-only
          </Badge>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <Button asChild size="sm" data-testid="shared-cta-button">
              <Link to="/login">Build your own</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 bg-surface-2 px-2 py-3 sm:px-3 lg:px-5">
        {ws.layout?.length ? (
          <WorkspaceGrid
            items={ws.layout}
            editMode={false}
            defaults={defaults}
            accounts={accounts}
            shareToken={token}
            onDrill={(item) => setDrillItem(item)}
          />
        ) : (
          <div className="flex min-h-[300px] items-center justify-center text-sm text-muted-foreground">
            This workspace has no widgets yet.
          </div>
        )}
      </main>

      <footer className="border-t bg-card px-4 py-3 text-center text-xs text-muted-foreground">
        Powered by <span className="font-semibold text-foreground">My Trading Room</span>. Build your own trading workspace in minutes.
      </footer>

      <DrilldownModal item={drillItem} defaults={defaults} accounts={accounts} shareToken={token}
        open={!!drillItem} onClose={() => setDrillItem(null)} />
    </div>
  );
}
